import { useState } from "react";

interface HeroProps {
  onSearch: (keyword: string) => void;
}

const hotKeywords = ["塌房", "实锤", "猹与闰土", "破防", "回旋镖"];

const stats = [
  { value: "20+", label: "收录热词" },
  { value: "6", label: "覆盖分类" },
  { value: "4+", label: "深读专题" },
  { value: "持续", label: "每日追更" },
];

export default function Hero({ onSearch }: HeroProps) {
  const [value, setValue] = useState("");

  const submit = () => {
    const kw = value.trim();
    if (kw) {
      onSearch(kw);
    }
  };

  const pick = (kw: string) => {
    setValue(kw);
    onSearch(kw);
  };

  return (
    <section id="top" className="relative overflow-hidden">
      {/* 装饰底色 */}
      <div className="absolute inset-0 -z-10 bg-gradient-to-br from-background-100 via-background-50 to-secondary-50"></div>
      <div className="absolute -right-24 -top-24 -z-10 h-80 w-80 rounded-full bg-primary-100/70 blur-2xl"></div>
      <div className="absolute -left-20 top-40 -z-10 h-64 w-64 rounded-full bg-accent-100/70 blur-2xl"></div>

      <div className="mx-auto max-w-7xl px-4 pb-12 pt-28 md:px-6 md:pb-16 md:pt-36">
        <div className="grid items-center gap-10 lg:grid-cols-2 lg:gap-12">
          {/* 左侧文案 */}
          <div className="w-full">
            <span className="inline-flex items-center gap-1.5 rounded-full bg-accent-100 px-3 py-1 text-xs font-medium text-accent-800">
              <i className="ri-book-open-line text-sm leading-none"></i>
              吃瓜科普 · 名词速览 · 冷知识速递
            </span>

            <h1 className="mt-5 font-heading text-3xl font-bold leading-snug text-foreground-950 sm:text-4xl lg:text-[2.9rem] lg:leading-[1.2]">
              看得懂热闹
              <span className="text-primary-700">，也看得懂门道</span>
            </h1>

            <p className="mt-5 max-w-xl text-[15px] leading-7 text-foreground-600">
              「吃瓜百科」用通俗语言，把热点事件里的网络热梗、行业黑话、典故来源与法律常识
              一个个拆开讲清楚。一篇带你搞懂吃瓜路上的冷知识，围观从此不再只看表面。
            </p>

            {/* 搜索 */}
            <form
              className="mt-7 flex w-full max-w-xl flex-col gap-3 sm:flex-row"
              onSubmit={(e) => {
                e.preventDefault();
                submit();
              }}
            >
              <label className="flex flex-1 items-center gap-2 rounded-lg bg-background-50 px-4 ring-1 ring-background-300 transition-shadow focus-within:ring-2 focus-within:ring-primary-400">
                <i className="ri-search-line text-base leading-none text-primary-600"></i>
                <input
                  value={value}
                  onChange={(e) => setValue(e.target.value)}
                  type="text"
                  placeholder="想查哪个词？如「塌房」「实锤」"
                  className="w-full bg-transparent py-3 text-sm text-foreground-900 placeholder:text-foreground-400 outline-none"
                />
              </label>
              <button
                type="submit"
                className="whitespace-nowrap rounded-lg bg-primary-600 px-6 py-3 text-sm font-medium text-background-50 transition-colors hover:bg-primary-700 cursor-pointer"
              >
                去查词
              </button>
            </form>

            {/* 关键词 */}
            <div className="mt-6 flex flex-wrap items-center gap-2">
              <span className="text-xs text-foreground-500">今日热词：</span>
              {hotKeywords.map((kw) => (
                <button
                  key={kw}
                  onClick={() => pick(kw)}
                  className="rounded-full bg-background-100 px-3 py-1 text-xs font-medium text-primary-700 ring-1 ring-primary-200/70 transition-colors hover:bg-primary-100 cursor-pointer"
                >
                  #{kw}
                </button>
              ))}
            </div>
          </div>

          {/* 右侧插画 */}
          <div className="relative mx-auto w-full max-w-md lg:max-w-none">
            <div className="relative overflow-hidden rounded-2xl bg-background-50 ring-1 ring-background-200/80">
              <img
                src="https://readdy.ai/api/search-image?query=flat%20editorial%20illustration%20of%20a%20young%20person%20casually%20enjoying%20watermelon%20while%20scrolling%20a%20phone%2C%20surrounded%20by%20floating%20speech%20bubbles%20with%20question%20marks%2C%20chat%20icons%20and%20a%20magnifying%20glass%2C%20symbolizing%20internet%20gossip%20and%20trending%20topics%2C%20warm%20cream%20background%2C%20deep%20melon%20green%20and%20coral%20red%20color%20palette%2C%20minimal%20clean%20modern%20vector%20art%2C%20soft%20shadows%2C%20high%20quality&width=840&height=980&seq=chi-hero-illustration&orientation=portrait"
                alt="吃瓜群众在手机前边吃西瓜边围观网络热点的插画"
                className="h-full w-full object-cover object-top"
              />
              <div className="absolute bottom-3 left-3 rounded-full bg-background-50/90 px-3 py-1.5 text-xs font-medium text-foreground-700 ring-1 ring-background-200 backdrop-blur">
                🍉 吃瓜请认准科普版
              </div>
            </div>
          </div>
        </div>

        {/* 数据条 */}
        <div className="mt-12 grid grid-cols-2 gap-3 border-t border-background-200/80 pt-8 sm:grid-cols-4 md:mt-16">
          {stats.map((s) => (
            <div key={s.label} className="text-center sm:text-left">
              <div className="font-heading text-2xl font-bold text-primary-700 md:text-3xl">
                {s.value}
              </div>
              <div className="mt-1 text-xs text-foreground-500 md:text-sm">{s.label}</div>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}